/**
 * Optional media step (before summary): tabbed video / voice / file. Blob stays
 * in the tab until Submit (WEST-823).
 */

const DEFAULT_MAX_RECORD_SECONDS = 120;
const DEFAULT_MAX_BYTES = 262_144_000; // 250 MB — keep in sync with QuickApplyMediaUploadService::DEFAULT_MAX_BYTES

function escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function pickMimeType(candidates) {
    if (!window.MediaRecorder || typeof MediaRecorder.isTypeSupported !== 'function') {
        return '';
    }
    for (const type of candidates) {
        if (MediaRecorder.isTypeSupported(type)) {
            return type;
        }
    }
    return '';
}

/**
 * Prefer A/V when both kinds exist; otherwise request only what is present.
 * After site permission, enumerateDevices() should list real deviceIds.
 *
 * @returns {Promise<MediaStream>}
 */
async function requestVideoStream() {
    let devices = [];
    try {
        devices = await navigator.mediaDevices.enumerateDevices();
    } catch (e) {
        // ignore — still try getUserMedia
    }
    const cams = devices.filter((d) => d.kind === 'videoinput');
    const mics = devices.filter((d) => d.kind === 'audioinput');

    /** @type {MediaStreamConstraints[]} */
    const attempts = [];
    if (cams.length > 0 && mics.length > 0) {
        attempts.push({ video: true, audio: true });
    }
    if (cams.length > 0) {
        attempts.push({ video: true, audio: false });
        for (const cam of cams) {
            if (cam.deviceId) {
                attempts.push({
                    video: { deviceId: { ideal: cam.deviceId } },
                    audio: false,
                });
            }
        }
    }
    // Before labels/ids exist (or enumerate failed), still try unconstrained.
    if (attempts.length === 0) {
        attempts.push({ video: true, audio: true }, { video: true, audio: false });
    }

    let lastErr = null;
    for (const constraints of attempts) {
        try {
            return await navigator.mediaDevices.getUserMedia(constraints);
        } catch (err) {
            lastErr = err;
        }
    }
    if (lastErr) {
        /** @type {any} */
        const enriched = lastErr;
        enriched._qaDeviceSummary = `cameras=${cams.length}, mics=${mics.length}`;
        throw enriched;
    }
    throw new Error('getUserMedia failed');
}

/**
 * @param {unknown} err
 * @param {Record<string, string>} labels
 */
function mediaAccessErrorMessage(err, labels) {
    const name = String(/** @type {{ name?: string }} */ (err)?.name || '');
    const summary = String(/** @type {{ _qaDeviceSummary?: string }} */ (err)?._qaDeviceSummary || '');
    let base = labels.mediaPermissionDenied || 'Camera or microphone access was denied or is unavailable.';
    if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
        base = labels.mediaDeviceMissing
            || 'No camera was available for this site. In the address-bar site settings, set Camera to “Ask” (not a specific device), click Reset permissions, reload, then allow again.';
    } else if (name === 'NotReadableError' || name === 'TrackStartError' || name === 'AbortError') {
        base = labels.mediaDeviceBusy
            || 'Camera or microphone is already in use by another application.';
    } else if (name === 'SecurityError') {
        base = labels.mediaInsecureContext
            || 'Camera access requires a secure connection (HTTPS).';
    } else if (name === 'OverconstrainedError') {
        base = labels.mediaPermissionDenied
            || 'Camera or microphone access was denied or is unavailable.';
    }
    const bits = [name, summary].filter(Boolean).join(', ');
    return bits ? `${base} (${bits})` : base;
}

function streamHasLiveTracks(stream) {
    return !!(stream && stream.getTracks().some((t) => t.readyState === 'live'));
}

function stopStream(stream) {
    if (!stream) {
        return;
    }
    stream.getTracks().forEach((t) => t.stop());
}

function formatTimer(seconds) {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/**
 * Media mount only (tabs / recorder) — wrapped in the chat agent bubble by the host.
 *
 * @param {{ labels: Record<string, string> }} opts
 * @returns {string}
 */
export function renderMediaMount({ labels }) {
    const tabVideo = labels.mediaRecordVideo || 'Video';
    const tabAudio = labels.mediaRecordAudio || 'Voice';
    const tabFile = labels.mediaChooseFile || 'File';
    const startLabel = labels.mediaStart || 'Start';
    const stopLabel = labels.mediaStop || 'Stop';
    const retakeLabel = labels.mediaRetake || 'Retake';
    const browseLabel = labels.mediaBrowseFile || 'Choose file';
    const micHint = labels.mediaMicHint || 'Speak — the meter should move if your mic works.';

    return `
		<div class="quick-apply__media-mount" data-qa-media>
			<div class="quick-apply__media-tabs" role="tablist">
				<button type="button" class="quick-apply__media-tab is-active" role="tab" aria-selected="true" data-qa-tab="video">${escapeHtml(tabVideo)}</button>
				<button type="button" class="quick-apply__media-tab" role="tab" aria-selected="false" data-qa-tab="audio">${escapeHtml(tabAudio)}</button>
				<button type="button" class="quick-apply__media-tab" role="tab" aria-selected="false" data-qa-tab="file">${escapeHtml(tabFile)}</button>
			</div>

			<div class="quick-apply__media-panel" data-qa-panel="video" role="tabpanel">
				<video class="quick-apply__media-preview" data-qa-preview-video playsinline muted></video>
				<p class="quick-apply__media-timer" data-qa-timer-video>00:00</p>
				<div class="quick-apply__media-controls">
					<button type="button" class="quick-apply__nav-btn quick-apply__nav-btn--next" data-qa-start-video>${escapeHtml(startLabel)}</button>
					<button type="button" class="quick-apply__nav-btn quick-apply__nav-btn--back" data-qa-stop-video hidden>${escapeHtml(stopLabel)}</button>
				</div>
			</div>

			<div class="quick-apply__media-panel" data-qa-panel="audio" role="tabpanel" hidden>
				<div class="quick-apply__media-meter" data-qa-audio-meter aria-hidden="true">
					<div class="quick-apply__media-meter-track">
						<div class="quick-apply__media-meter-fill" data-qa-meter-fill></div>
					</div>
					<div class="quick-apply__media-meter-bars" data-qa-meter-bars></div>
					<p class="quick-apply__media-meter-device" data-qa-meter-device></p>
					<p class="quick-apply__media-meter-hint" data-qa-meter-hint>${escapeHtml(micHint)}</p>
				</div>
				<p class="quick-apply__media-timer" data-qa-timer-audio>00:00</p>
				<div class="quick-apply__media-controls">
					<button type="button" class="quick-apply__nav-btn quick-apply__nav-btn--next" data-qa-start-audio>${escapeHtml(startLabel)}</button>
					<button type="button" class="quick-apply__nav-btn quick-apply__nav-btn--back" data-qa-stop-audio hidden>${escapeHtml(stopLabel)}</button>
				</div>
			</div>

			<div class="quick-apply__media-panel" data-qa-panel="file" role="tabpanel" hidden>
				<div class="quick-apply__media-controls">
					<button type="button" class="quick-apply__nav-btn quick-apply__nav-btn--next" data-qa-choose-file>${escapeHtml(browseLabel)}</button>
					<input type="file" accept="video/*,audio/*" hidden data-qa-file-input />
				</div>
			</div>

			<div class="quick-apply__media-result" data-qa-result hidden>
				<video class="quick-apply__media-playback" data-qa-playback controls playsinline hidden></video>
				<audio class="quick-apply__media-playback" data-qa-playback-audio controls hidden></audio>
				<p class="quick-apply__media-filemeta" data-qa-filemeta></p>
				<button type="button" class="quick-apply__nav-btn quick-apply__nav-btn--back" data-qa-retake>${escapeHtml(retakeLabel)}</button>
			</div>
			<div class="quick-apply__media-progress" data-qa-progress-wrap hidden>
				<p class="quick-apply__media-progress-label" data-qa-progress-label></p>
				<div class="quick-apply__media-progress-track">
					<div class="quick-apply__media-progress-bar" data-qa-progress-bar></div>
				</div>
			</div>
			<p class="quick-apply__media-status" data-qa-media-status hidden></p>
		</div>
	`;
}

/**
 * @param {{ labels: Record<string, string>, chatHtml: string, progressHtml?: string }} opts
 * @returns {string}
 */
export function renderMediaStep({ labels, chatHtml, progressHtml = '' }) {
    const skipLabel = labels.skip || 'Skip';
    const continueLabel = labels.next || 'Next';

    return `
		<div class="quick-apply__media" data-qa-media-step>
			<div class="quick-apply__chat" data-qa-chat>
				${chatHtml}
			</div>
			<nav class="quick-apply__nav" aria-label="Quick Apply">
				<div class="quick-apply__nav-start">
					<button type="button" class="quick-apply__nav-btn quick-apply__nav-btn--back" data-qa-skip>${escapeHtml(skipLabel)}</button>
				</div>
				<div class="quick-apply__nav-center">${progressHtml}</div>
				<div class="quick-apply__nav-end">
					<button type="button" class="quick-apply__nav-btn quick-apply__nav-btn--next" data-qa-next disabled>${escapeHtml(continueLabel)}</button>
				</div>
			</nav>
		</div>
	`;
}

/**
 * @param {HTMLElement} stage
 * @param {{
 *   onAdvance: (opts?: { skipped?: boolean, file?: File|null, kind?: string|null, durationSeconds?: number|null }) => void,
 *   maxRecordSeconds?: number,
 *   maxUploadBytes?: number,
 *   labels: Record<string, string>,
 *   initialFile?: File|null,
 *   initialKind?: string|null,
 *   initialDurationSeconds?: number|null,
 * }} opts
 */
export function bindMediaStep(stage, {
    onAdvance,
    maxRecordSeconds: maxRecordSecondsOpt,
    maxUploadBytes: maxUploadBytesOpt,
    labels,
    initialFile = null,
    initialKind = null,
    initialDurationSeconds = null,
}) {
    let maxRecordSeconds = Number(maxRecordSecondsOpt) || DEFAULT_MAX_RECORD_SECONDS;
    maxRecordSeconds = Math.max(10, Math.min(3600, maxRecordSeconds));
    let maxUploadBytes = Number(maxUploadBytesOpt) || DEFAULT_MAX_BYTES;
    if (maxUploadBytes <= 0) {
        maxUploadBytes = DEFAULT_MAX_BYTES;
    }

    const mount = stage.querySelector('[data-qa-media]');
    if (!mount) {
        return;
    }

    const els = {
        tabs: [...mount.querySelectorAll('[data-qa-tab]')],
        panels: {
            video: mount.querySelector('[data-qa-panel="video"]'),
            audio: mount.querySelector('[data-qa-panel="audio"]'),
            file: mount.querySelector('[data-qa-panel="file"]'),
        },
        previewVideo: mount.querySelector('[data-qa-preview-video]'),
        timerVideo: mount.querySelector('[data-qa-timer-video]'),
        timerAudio: mount.querySelector('[data-qa-timer-audio]'),
        startVideo: mount.querySelector('[data-qa-start-video]'),
        stopVideo: mount.querySelector('[data-qa-stop-video]'),
        startAudio: mount.querySelector('[data-qa-start-audio]'),
        stopAudio: mount.querySelector('[data-qa-stop-audio]'),
        meterFill: mount.querySelector('[data-qa-meter-fill]'),
        meterBars: mount.querySelector('[data-qa-meter-bars]'),
        meterDevice: mount.querySelector('[data-qa-meter-device]'),
        chooseFile: mount.querySelector('[data-qa-choose-file]'),
        fileInput: mount.querySelector('[data-qa-file-input]'),
        result: mount.querySelector('[data-qa-result]'),
        playback: mount.querySelector('[data-qa-playback]'),
        playbackAudio: mount.querySelector('[data-qa-playback-audio]'),
        filemeta: mount.querySelector('[data-qa-filemeta]'),
        retake: mount.querySelector('[data-qa-retake]'),
        progressWrap: mount.querySelector('[data-qa-progress-wrap]'),
        progressBar: mount.querySelector('[data-qa-progress-bar]'),
        progressLabel: mount.querySelector('[data-qa-progress-label]'),
        status: mount.querySelector('[data-qa-media-status]'),
    };
    const skip = stage.querySelector('[data-qa-skip]');
    const next = stage.querySelector('[data-qa-next]');

    let activeTab = 'video';
    let previewStream = null;
    let audioPreviewStream = null;
    let recordStream = null;
    let recorder = null;
    let chunks = [];
    let timerId = null;
    let startedAt = 0;
    let pendingFile = null;
    /** @type {'video'|'audio'|'file'|null} */
    let pendingKind = null;
    /** @type {number|null} */
    let pendingDurationSeconds = null;
    let objectUrl = '';
    let sessionId = 0;
    let previewStarting = false;
    let audioPreviewStarting = false;
    let audioCtx = null;
    let analyser = null;
    let meterSource = null;
    let meterRaf = 0;
    let meterSmooth = 0;
    let recordingStarting = false;

    const METER_BAR_COUNT = 16;
    if (els.meterBars) {
        els.meterBars.innerHTML = Array.from({ length: METER_BAR_COUNT }, () => '<span></span>').join('');
    }

    function setStatus(message, isError) {
        if (!els.status) {
            return;
        }
        if (!message) {
            els.status.hidden = true;
            els.status.textContent = '';
            return;
        }
        els.status.hidden = false;
        els.status.textContent = message;
        els.status.classList.toggle('quick-apply__media-status--error', !!isError);
    }

    function setProgress(pct) {
        if (!els.progressWrap || !els.progressBar) {
            return;
        }
        if (pct == null) {
            els.progressWrap.hidden = true;
            els.progressWrap.classList.remove('is-indeterminate');
            els.progressBar.style.width = '0%';
            if (els.progressLabel) {
                els.progressLabel.textContent = '';
            }
            return;
        }
        const value = Math.max(0, Math.min(100, pct));
        els.progressWrap.hidden = false;
        els.progressWrap.classList.toggle('is-indeterminate', value <= 0);
        els.progressBar.style.width = value <= 0 ? '30%' : `${value}%`;
        if (els.progressLabel) {
            const base = labels.mediaUploading || 'Uploading…';
            els.progressLabel.textContent = value <= 0 ? base : `${base} ${value}%`;
        }
    }

    function clearObjectUrl() {
        if (objectUrl) {
            URL.revokeObjectURL(objectUrl);
            objectUrl = '';
        }
    }

    function clearMediaEl(el) {
        if (!el) {
            return;
        }
        try {
            el.pause();
        } catch (e) {
            // ignore
        }
        el.removeAttribute('src');
        el.srcObject = null;
        el.hidden = true;
        try {
            el.load();
        } catch (e) {
            // ignore
        }
    }

    function setRecordingControls(kind, recording) {
        if (kind === 'video') {
            if (els.startVideo) {
                els.startVideo.hidden = recording;
                els.startVideo.disabled = recording;
            }
            if (els.stopVideo) {
                els.stopVideo.hidden = !recording;
            }
        } else if (kind === 'audio') {
            if (els.startAudio) {
                els.startAudio.hidden = recording;
                els.startAudio.disabled = recording;
            }
            if (els.stopAudio) {
                els.stopAudio.hidden = !recording;
            }
        }
    }

    function resetTimers() {
        const maxLabel = formatTimer(maxRecordSeconds);
        if (els.timerVideo) {
            els.timerVideo.textContent = `00:00 / ${maxLabel}`;
        }
        if (els.timerAudio) {
            els.timerAudio.textContent = `00:00 / ${maxLabel}`;
        }
    }

    function updateActionLabels() {
        if (next) {
            next.textContent = labels.next || 'Next';
        }
        if (els.retake) {
            els.retake.textContent = pendingKind === 'file'
                ? (labels.mediaReplaceFile || 'Replace file')
                : (labels.mediaRetake || 'Retake');
        }
    }

    function setHasPending(on) {
        mount.classList.toggle('has-pending', !!on);
    }

    function stopMeter() {
        if (meterRaf) {
            cancelAnimationFrame(meterRaf);
            meterRaf = 0;
        }
        meterSmooth = 0;
        if (meterSource) {
            try {
                meterSource.disconnect();
            } catch (e) {
                // ignore
            }
            meterSource = null;
        }
        analyser = null;
        if (audioCtx) {
            try {
                audioCtx.close();
            } catch (e) {
                // ignore
            }
            audioCtx = null;
        }
        if (els.meterFill) {
            els.meterFill.style.width = '0%';
        }
        if (els.meterBars) {
            els.meterBars.querySelectorAll('span').forEach((bar) => {
                bar.style.transform = 'scaleY(0.08)';
                bar.classList.remove('is-hot');
            });
        }
    }

    function setMeterLevel(level) {
        const clamped = Math.max(0, Math.min(1, level));
        meterSmooth = meterSmooth * 0.7 + clamped * 0.3;
        if (els.meterFill) {
            els.meterFill.style.width = `${Math.round(meterSmooth * 100)}%`;
        }
        if (els.meterBars) {
            const bars = els.meterBars.querySelectorAll('span');
            bars.forEach((bar, i) => {
                const threshold = (i + 1) / bars.length;
                const active = meterSmooth >= threshold * 0.85;
                const height = active
                    ? Math.min(1, 0.15 + meterSmooth * (1.2 - threshold))
                    : 0.08;
                bar.style.transform = `scaleY(${height})`;
                bar.classList.toggle('is-hot', active && threshold > 0.75);
            });
        }
    }

    function startMeter(stream) {
        stopMeter();
        if (!stream || typeof window.AudioContext === 'undefined' && typeof window.webkitAudioContext === 'undefined') {
            return;
        }
        const Ctx = window.AudioContext || window.webkitAudioContext;
        try {
            audioCtx = new Ctx();
            analyser = audioCtx.createAnalyser();
            analyser.fftSize = 1024;
            analyser.smoothingTimeConstant = 0.8;
            meterSource = audioCtx.createMediaStreamSource(stream);
            meterSource.connect(analyser);
            if (audioCtx.state === 'suspended') {
                audioCtx.resume().catch(() => {});
            }
            const data = new Uint8Array(analyser.fftSize);
            const tick = () => {
                if (!analyser) {
                    return;
                }
                analyser.getByteTimeDomainData(data);
                let sum = 0;
                for (let i = 0; i < data.length; i += 1) {
                    const v = (data[i] - 128) / 128;
                    sum += v * v;
                }
                // Amplify a bit so quiet mics still move the meter.
                const rms = Math.sqrt(sum / data.length);
                setMeterLevel(Math.min(1, rms * 4.5));
                meterRaf = requestAnimationFrame(tick);
            };
            meterRaf = requestAnimationFrame(tick);
        } catch (e) {
            stopMeter();
        }
    }

    function setMicDeviceLabel(stream) {
        if (!els.meterDevice) {
            return;
        }
        const track = stream?.getAudioTracks?.()[0];
        const label = track?.label ? String(track.label).trim() : '';
        els.meterDevice.textContent = label || '';
        els.meterDevice.hidden = label === '';
    }

    function stopVideoPreview() {
        if (els.previewVideo) {
            els.previewVideo.srcObject = null;
        }
        stopStream(previewStream);
        previewStream = null;
    }

    function stopAudioPreview() {
        stopMeter();
        stopStream(audioPreviewStream);
        audioPreviewStream = null;
        if (els.meterDevice) {
            els.meterDevice.textContent = '';
            els.meterDevice.hidden = true;
        }
    }

    function stopPreview() {
        stopVideoPreview();
        stopAudioPreview();
    }

    function stopRecordingHardware() {
        clearInterval(timerId);
        timerId = null;
        const active = recorder;
        recorder = null;
        chunks = [];
        if (active && active.state !== 'inactive') {
            try {
                active.stop();
            } catch (e) {
                // ignore
            }
        }
        stopStream(recordStream);
        recordStream = null;
        setRecordingControls('video', false);
        setRecordingControls('audio', false);
        resetTimers();
    }

    function cleanupCapture() {
        stopRecordingHardware();
        stopPreview();
    }

    function resetResult() {
        clearObjectUrl();
        pendingFile = null;
        pendingKind = null;
        pendingDurationSeconds = null;
        setHasPending(false);
        updateActionLabels();
        if (next) {
            next.disabled = true;
        }
        if (els.result) {
            els.result.hidden = true;
        }
        clearMediaEl(els.playback);
        clearMediaEl(els.playbackAudio);
        if (els.filemeta) {
            els.filemeta.textContent = '';
        }
        setProgress(null);
    }

    function validateLocalFile(file) {
        if (!file) {
            return false;
        }
        if (file.size > maxUploadBytes) {
            const mb = Math.round(maxUploadBytes / (1024 * 1024));
            setStatus(
                `${labels.mediaTooLarge || 'File is too large'}${mb ? ` (max ${mb} MB)` : ''}`,
                true
            );
            return false;
        }
        const type = String(file.type || '');
        if (type && !type.startsWith('video/') && !type.startsWith('audio/')) {
            setStatus(labels.mediaTypeDenied || 'Only video or audio is allowed', true);
            return false;
        }
        return true;
    }

    function acceptPendingFile(file, kind, durationSeconds = null) {
        if (!validateLocalFile(file)) {
            return false;
        }
        pendingFile = file;
        pendingKind = kind;
        pendingDurationSeconds = durationSeconds;
        showResult(file, kind);
        setHasPending(true);
        updateActionLabels();
        if (next) {
            next.disabled = false;
        }
        setStatus('', false);
        return true;
    }

    function showResult(file, kind) {
        if (!els.result) {
            return;
        }
        clearMediaEl(els.playback);
        clearMediaEl(els.playbackAudio);
        clearObjectUrl();
        objectUrl = URL.createObjectURL(file);
        els.result.hidden = false;
        const playbackKind = kind === 'audio'
            || (kind === 'file' && String(file.type || '').startsWith('audio/'))
            ? 'audio'
            : 'video';
        if (playbackKind === 'audio') {
            if (els.playbackAudio) {
                els.playbackAudio.hidden = false;
                els.playbackAudio.src = objectUrl;
            }
        } else if (els.playback) {
            els.playback.hidden = false;
            els.playback.src = objectUrl;
        }
        if (els.filemeta) {
            const mb = (file.size / (1024 * 1024)).toFixed(1);
            els.filemeta.textContent = `${file.name || 'recording'} (${mb} MB)`;
        }
    }

    async function ensureVideoPreview() {
        if (previewStream || previewStarting) {
            return;
        }
        if (!navigator.mediaDevices?.getUserMedia) {
            setStatus(labels.mediaPermissionDenied || 'Camera unavailable', true);
            return;
        }
        previewStarting = true;
        try {
            previewStream = await requestVideoStream();
            if (activeTab !== 'video') {
                stopStream(previewStream);
                previewStream = null;
                return;
            }
            if (els.previewVideo) {
                els.previewVideo.hidden = false;
                els.previewVideo.muted = true;
                els.previewVideo.srcObject = previewStream;
                try {
                    await els.previewVideo.play();
                } catch (e) {
                    // ignore autoplay
                }
            }
            setStatus('', false);
        } catch (e) {
            setStatus(mediaAccessErrorMessage(e, labels), true);
        } finally {
            previewStarting = false;
        }
    }

    async function ensureAudioPreview() {
        if (audioPreviewStream || audioPreviewStarting) {
            if (audioPreviewStream) {
                startMeter(audioPreviewStream);
                setMicDeviceLabel(audioPreviewStream);
            }
            return;
        }
        if (!navigator.mediaDevices?.getUserMedia) {
            setStatus(labels.mediaPermissionDenied || 'Microphone unavailable', true);
            return;
        }
        audioPreviewStarting = true;
        try {
            audioPreviewStream = await navigator.mediaDevices.getUserMedia({ audio: true });
            if (activeTab !== 'audio') {
                stopStream(audioPreviewStream);
                audioPreviewStream = null;
                return;
            }
            setMicDeviceLabel(audioPreviewStream);
            startMeter(audioPreviewStream);
            setStatus('', false);
        } catch (e) {
            setStatus(mediaAccessErrorMessage(e, labels), true);
        } finally {
            audioPreviewStarting = false;
        }
    }

    async function switchTab(tab) {
        if (!['video', 'audio', 'file'].includes(tab)) {
            return;
        }
        const mySession = ++sessionId;
        stopRecordingHardware();
        resetResult();
        setStatus('', false);

        activeTab = tab;
        els.tabs.forEach((btn) => {
            const on = btn.getAttribute('data-qa-tab') === tab;
            btn.classList.toggle('is-active', on);
            btn.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        Object.keys(els.panels).forEach((key) => {
            const panel = els.panels[key];
            if (panel) {
                panel.hidden = key !== tab;
            }
        });

        if (tab === 'video') {
            stopAudioPreview();
            await ensureVideoPreview();
            if (mySession !== sessionId) {
                return;
            }
        } else if (tab === 'audio') {
            stopVideoPreview();
            await ensureAudioPreview();
            if (mySession !== sessionId) {
                return;
            }
        } else {
            stopPreview();
        }
    }

    async function startRecording(kind) {
        const mySession = sessionId;
        if (recordingStarting) {
            return;
        }
        if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
            setStatus(labels.mediaPermissionDenied || 'Recording unavailable', true);
            return;
        }

        recordingStarting = true;
        resetResult();
        setStatus(labels.mediaOpeningDevice || 'Opening camera / microphone…', false);
        stopRecordingHardware();

        let stream;
        try {
            if (kind === 'video') {
                // Reuse live preview stream so Start does not re-prompt.
                if (streamHasLiveTracks(previewStream)) {
                    stream = previewStream;
                    previewStream = null;
                } else {
                    stopStream(previewStream);
                    previewStream = null;
                    stream = await requestVideoStream();
                }
            } else if (streamHasLiveTracks(audioPreviewStream)) {
                stream = audioPreviewStream;
                audioPreviewStream = null;
            } else {
                stopStream(audioPreviewStream);
                audioPreviewStream = null;
                stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            }
        } catch (e) {
            setStatus(mediaAccessErrorMessage(e, labels), true);
            recordingStarting = false;
            return;
        }

        if (mySession !== sessionId) {
            stopStream(stream);
            recordingStarting = false;
            return;
        }

        recordStream = stream;
        if (kind === 'video' && els.previewVideo) {
            els.previewVideo.hidden = false;
            els.previewVideo.muted = true;
            els.previewVideo.srcObject = stream;
            try {
                await els.previewVideo.play();
            } catch (e) {
                // ignore
            }
        }
        if (kind === 'audio') {
            setMicDeviceLabel(stream);
            startMeter(stream);
        }

        const mime = pickMimeType(
            kind === 'audio'
                ? ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4']
                : ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4']
        );
        chunks = [];
        let recordedBytes = 0;
        const sizeStopAt = Math.floor(maxUploadBytes * 0.95);
        let localRecorder;
        try {
            localRecorder = mime
                ? new MediaRecorder(stream, { mimeType: mime })
                : new MediaRecorder(stream);
        } catch (e) {
            stopRecordingHardware();
            setStatus(labels.mediaPermissionDenied || 'Recording unavailable', true);
            recordingStarting = false;
            return;
        }
        recorder = localRecorder;
        setRecordingControls(kind, true);
        setStatus('', false);
        recordingStarting = false;

        localRecorder.addEventListener('dataavailable', (ev) => {
            if (mySession !== sessionId) {
                return;
            }
            if (ev.data && ev.data.size > 0) {
                chunks.push(ev.data);
                recordedBytes += ev.data.size;
                // MediaRecorder has no max-filesize option — stop before upload limit.
                if (recordedBytes >= sizeStopAt && localRecorder.state === 'recording') {
                    try {
                        localRecorder.stop();
                    } catch (err) {
                        // ignore
                    }
                }
            }
        });
        localRecorder.addEventListener('stop', async () => {
            if (mySession !== sessionId) {
                return;
            }
            clearInterval(timerId);
            timerId = null;
            const blobType = localRecorder.mimeType || mime || (kind === 'audio' ? 'audio/webm' : 'video/webm');
            const blob = new Blob(chunks, { type: blobType });
            stopStream(recordStream);
            recordStream = null;
            recorder = null;
            setRecordingControls(kind, false);
            resetTimers();

            if (els.previewVideo) {
                els.previewVideo.srcObject = null;
            }

            const resultKind = kind === 'audio' ? 'audio' : 'video';
            const ext = blobType.includes('mp4') ? 'mp4' : (blobType.includes('ogg') ? 'ogg' : 'webm');
            const fileType = resultKind === 'audio' && blobType.startsWith('video/')
                ? blobType.replace(/^video\//, 'audio/')
                : blobType;
            const file = new File([blob], `recording-${resultKind}.${ext}`, { type: fileType || blobType });
            const durationSeconds = startedAt ? Math.max(0, Math.round((Date.now() - startedAt) / 1000)) : null;
            acceptPendingFile(file, resultKind, durationSeconds);
            stopPreview();
        }, { once: true });

        localRecorder.start(200);
        startedAt = Date.now();
        const timerEl = kind === 'audio' ? els.timerAudio : els.timerVideo;
        const maxLabel = formatTimer(maxRecordSeconds);
        timerId = setInterval(() => {
            if (mySession !== sessionId) {
                clearInterval(timerId);
                return;
            }
            const elapsed = Math.floor((Date.now() - startedAt) / 1000);
            if (timerEl) {
                timerEl.textContent = `${formatTimer(elapsed)} / ${maxLabel}`;
            }
            if (elapsed >= maxRecordSeconds && localRecorder.state === 'recording') {
                localRecorder.stop();
            }
        }, 250);
    }

    els.tabs.forEach((btn) => {
        btn.addEventListener('click', () => {
            const tab = btn.getAttribute('data-qa-tab') || 'video';
            switchTab(tab);
        });
    });

    els.startVideo?.addEventListener('click', () => startRecording('video'));
    els.stopVideo?.addEventListener('click', () => {
        if (recorder && recorder.state === 'recording') {
            recorder.stop();
        }
    });
    els.startAudio?.addEventListener('click', () => startRecording('audio'));
    els.stopAudio?.addEventListener('click', () => {
        if (recorder && recorder.state === 'recording') {
            recorder.stop();
        }
    });

    els.chooseFile?.addEventListener('click', () => els.fileInput?.click());
    els.fileInput?.addEventListener('change', () => {
        const file = els.fileInput.files?.[0];
        els.fileInput.value = '';
        if (!file) {
            return;
        }
        stopRecordingHardware();
        resetResult();
        acceptPendingFile(file, 'file');
    });

    els.retake?.addEventListener('click', async () => {
        sessionId += 1;
        stopRecordingHardware();
        resetResult();
        setStatus('', false);
        if (activeTab === 'video') {
            await ensureVideoPreview();
        } else if (activeTab === 'audio') {
            await ensureAudioPreview();
        }
    });

    skip?.addEventListener('click', () => {
        sessionId += 1;
        cleanupCapture();
        clearObjectUrl();
        pendingFile = null;
        pendingDurationSeconds = null;
        if (typeof onAdvance === 'function') {
            onAdvance({ skipped: true, file: null, kind: null, durationSeconds: null });
        }
    });
    next?.addEventListener('click', () => {
        if (!pendingFile || next.disabled) {
            return;
        }
        const file = pendingFile;
        const kind = pendingKind;
        const durationSeconds = pendingDurationSeconds;
        sessionId += 1;
        cleanupCapture();
        clearObjectUrl();
        if (typeof onAdvance === 'function') {
            onAdvance({ skipped: false, file, kind, durationSeconds });
        }
    });

    if (initialFile) {
        acceptPendingFile(initialFile, initialKind || 'video', initialDurationSeconds);
    } else {
        switchTab('video');
    }
}
